'use client'

import { memo } from 'react'

import { StoreBonusStatusResponse, StoreRewardItem } from '@common/models/bonus'
import Image, { StaticImageData } from 'next/image'

import NotFound from '@/assets/images/skeleton-bomb-dud.png'

import { useGetDraftQuery, useUpdateDraftMutationState } from '../../../../api'
import { useGameSlug } from '../../game-provider'
import { BonusImage } from '../bonus/BonusImage'
import {
  extractBonusValues,
  getDeliveredBonuses,
  getNextItem,
  getProgressPercentage,
  getRemainingPoints,
  getBonusTrackCompletionStatus,
} from '../bonus/bonusUtils'
import { StoreTypography } from '../typography/StoreTypography'
import { DrawerButton } from './DrawerButton'

import styles from './bonusFooter.module.scss'

const HIDE_BONUS_IF_NO_DATA = false

const BonusFooterItemImage = ({ src }: { src?: string | StaticImageData }) => {
  return (
    <div className={styles.deliveredImageContainer}>
      <Image
        className={styles.deliveredImage}
        src={src || NotFound}
        alt="bonuses-delivered"
        fill
        sizes="50px"
        placeholder="empty"
      />
    </div>
  )
}

const BonusFooterItemImages = ({ items }: { items: StoreRewardItem[] }) => {
  return (
    <>
      {items.map((item) => (
        <BonusFooterItemImage key={item.id} src={item.image} />
      ))}
    </>
  )
}

const BonusItemCount = ({ count }: { count: number }) => {
  return (
    <div className={styles.deliveredCountContainer}>
      <StoreTypography
        tag="div"
        textSize="xs"
        style="plain"
        overrideFont="supercellTextMedium"
        className={styles.deliveredCount}
      >
        {count}
      </StoreTypography>
    </div>
  )
}

interface BonusFooterProps {
  bonusStatus: StoreBonusStatusResponse
  isDrawerOpen: boolean
  onOpenDrawer: (e: React.MouseEvent<HTMLDivElement>) => void
}

export const BonusFooter = memo(({ bonusStatus, isDrawerOpen, onOpenDrawer }: BonusFooterProps) => {
  const gameSlug = useGameSlug()
  const { data: draft } = useGetDraftQuery(gameSlug)
  const { isLoading } = useUpdateDraftMutationState()
  const { bonusItems } = extractBonusValues({ bonusStatus, draft })

  if (HIDE_BONUS_IF_NO_DATA && !bonusStatus) {
    return null
  }

  const tentativeBonusesDeliveredOnCheckout = bonusStatus ? getDeliveredBonuses({ bonusStatus, draft }) : []
  const progressPercentage = bonusStatus ? getProgressPercentage({ bonusStatus, draft }) : 0
  const remainingPoints = bonusStatus ? getRemainingPoints({ bonusStatus, draft }) : 0
  const nextItem = getNextItem({ bonusStatus, draft })
  const bonusTrackCompletionStatus = getBonusTrackCompletionStatus({ bonusStatus, draft })
  const isCompletedBonusTrack = bonusTrackCompletionStatus !== 'NOT_COMPLETE'

  const unclaimedItems = bonusItems.filter((item) => item.available && !item.claimed)
  const unclaimedBonuses = unclaimedItems.length > 0 && tentativeBonusesDeliveredOnCheckout.length === 0
  const bonusesDeliveredOnCheckout = [...unclaimedItems, ...tentativeBonusesDeliveredOnCheckout]

  if (HIDE_BONUS_IF_NO_DATA && !nextItem) {
    return null
  }

  // TODO: Translations

  const unclaimedBonusesText = 'Unclaimed Bonuses:'
  const remainingPointsText = `${remainingPoints} pts to next Store Bonus`
  const bonusesDeliveredText = 'Bonuses delivered on checkout:'
  const bonusesCompletedText = 'Bonus Track Completed!'
  const footerTitle = isCompletedBonusTrack ? bonusesCompletedText : remainingPointsText

  return (
    <div className={styles.closedContainer} onClick={onOpenDrawer}>
      {isCompletedBonusTrack ? (
        <div className={styles.bonusImageContainer}>
          <BonusImage status="completed" progressPercentage={100} loading={false} />
        </div>
      ) : (
        <div className={styles.bonusImageContainer}>
          <BonusImage item={nextItem} status="next" progressPercentage={progressPercentage} loading={isLoading} />
        </div>
      )}

      <div className={styles.textContainer}>
        <span className={styles.remainingPoints}>{footerTitle}</span>

        {unclaimedBonuses && unclaimedItems.length <= 3 && (
          <div className={styles.deliveredContainer}>
            <div className={styles.deliveredText}>{unclaimedBonusesText}</div>
            <BonusFooterItemImages items={unclaimedItems} />
          </div>
        )}

        {unclaimedBonuses && unclaimedItems.length > 3 && (
          <div className={styles.deliveredContainer}>
            <div className={styles.deliveredText}>{unclaimedBonusesText}</div>
            <BonusFooterItemImages items={[unclaimedItems[0]]} />
            <span className={styles.deliveredText}>+</span>
            <BonusItemCount count={unclaimedItems.length - 1} />
          </div>
        )}

        {!unclaimedBonuses && bonusesDeliveredOnCheckout.length > 0 && bonusesDeliveredOnCheckout.length <= 3 && (
          <div className={styles.deliveredContainer}>
            <div className={styles.deliveredText}>{bonusesDeliveredText}</div>
            <BonusFooterItemImages items={bonusesDeliveredOnCheckout} />
          </div>
        )}

        {!unclaimedBonuses && bonusesDeliveredOnCheckout.length > 3 && (
          <div className={styles.deliveredContainer}>
            <div className={styles.deliveredText}>{bonusesDeliveredText}</div>
            <BonusFooterItemImages items={[bonusesDeliveredOnCheckout[0]]} />
            <span className={styles.deliveredText}>+</span>
            <BonusItemCount count={bonusesDeliveredOnCheckout.length - 1} />
          </div>
        )}
      </div>

      <div className={styles.closedButtonContainer}>
        <DrawerButton isOpen={isDrawerOpen} />
      </div>
    </div>
  )
})

BonusFooter.displayName = 'BonusFooter'
